import React, { useState, useEffect } from 'react';
import { X, AlertCircle, Globe, Save, Plus, Trash2, Info, Mail, MessageCircle, Shield, Globe2, Activity, CheckCircle, XCircle, Zap, Server, Wifi, ShieldAlert } from 'lucide-react';

function EditWebsiteModal({ website, onClose, onSubmit, userLimits, darkMode = false }) {
  const greenColor = '#95DE1A';
  const greenGradient = 'linear-gradient(135deg, #95DE1A 0%, #7EC50A 100%)';

  const types = [
    { value: 'WEBSITE', label: 'Website (HTTP/HTTPS)', icon: Globe },
    { value: 'PORT', label: 'Port (TCP)', icon: Server },
    { value: 'API', label: 'API Endpoint', icon: Zap },
    { value: 'PING', label: 'Ping (ICMP)', icon: Wifi },
    { value: 'SERVICE', label: 'Service (Multi-component)', icon: Activity },
    { value: 'BLACKLIST', label: 'Blacklist Monitor', icon: ShieldAlert },
  ];

  const commonPorts = [
    { value: 22, label: '22 - SSH' },
    { value: 25, label: '25 - SMTP' },
    { value: 53, label: '53 - DNS' },
    { value: 80, label: '80 - HTTP' },
    { value: 443, label: '443 - HTTPS' },
    { value: 3306, label: '3306 - MySQL' },
    { value: 5432, label: '5432 - PostgreSQL' },
    { value: 6379, label: '6379 - Redis' },
    { value: 8080, label: '8080 - Custom App' },
    { value: 9200, label: '9200 - Elasticsearch' },
    { value: 27017, label: '27017 - MongoDB' },
  ];

  const [formData, setFormData] = useState({
    name: '',
    url: '',
    type: 'WEBSITE',
    isActive: true,
    port: '',
    portCustomMode: false,
    apiUrl: '',
    apiMethod: 'GET',
    apiExpectedStatus: 200,
    apiHeaders: '',
    ports: [],
    apis: [],
    pings: [],
    blacklistName: '',
    blacklistTarget: '',
    blacklistType: 'IP',
    blacklistInterval: 300,
    notifyEmail: true,
    notifyTelegram: true,
    notifySsl: true,
    notifyDomain: true,
    notifyDns: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (website) {
      const base = {
        name: website.name || '',
        url: website.url || '',
        type: website.type || 'WEBSITE',
        isActive: website.isActive !== undefined ? website.isActive : true,
        notifyEmail: website.notifyEmail !== undefined ? website.notifyEmail : true,
        notifyTelegram: website.notifyTelegram !== undefined ? website.notifyTelegram : true,
        notifySsl: website.notifySsl !== undefined ? website.notifySsl : true,
        notifyDomain: website.notifyDomain !== undefined ? website.notifyDomain : true,
        notifyDns: website.notifyDns !== undefined ? website.notifyDns : true,
      };

      if (website.type === 'PORT') {
        const monitor = website.portMonitors?.[0];
        const portVal = monitor?.port ? String(monitor.port) : '';
        const isCustom = portVal && !commonPorts.some(p => p.value === parseInt(portVal));
        base.port = portVal;
        base.portCustomMode = isCustom;
      } else if (website.type === 'API') {
        const monitor = website.apiMonitors?.[0];
        if (monitor) {
          base.apiUrl = monitor.url || '';
          base.apiMethod = monitor.method || 'GET';
          base.apiExpectedStatus = monitor.expectedStatus || 200;
          if (monitor.headers && typeof monitor.headers === 'object') {
            try {
              base.apiHeaders = JSON.stringify(monitor.headers, null, 2);
            } catch (e) {
              base.apiHeaders = '';
            }
          } else {
            base.apiHeaders = monitor.headers || '';
          }
        }
      } else if (website.type === 'PING') {
        const monitor = website.pingMonitors?.[0];
        base.url = monitor?.host || '';
      } else if (website.type === 'SERVICE') {
        base.ports = (website.portMonitors || []).map(p => ({
          id: p.id,
          port: p.port,
          customMode: !commonPorts.some(cp => cp.value === p.port)
        }));
        base.apis = (website.apiMonitors || []).map(a => ({
          id: a.id,
          url: a.url,
          method: a.method,
          expectedStatus: a.expectedStatus,
          headers: a.headers ? JSON.stringify(a.headers, null, 2) : '',
        }));
        base.pings = (website.pingMonitors || []).map(p => ({
          id: p.id,
          host: p.host,
        }));
      } else if (website.type === 'BLACKLIST') {
        base.blacklistName = website.name || '';
        base.blacklistTarget = website.target || '';
        base.blacklistType = website.type === 'IP' ? 'IP' : 'DOMAIN';
        base.blacklistInterval = website.interval || 300;
      }

      setFormData(base);
    }
  }, [website]);

  const handlePortChange = (value) => setFormData({ ...formData, port: value });
  const handlePortSelect = (e) => {
    const val = e.target.value;
    if (val === 'custom') setFormData({ ...formData, portCustomMode: true, port: '' });
    else setFormData({ ...formData, portCustomMode: false, port: val });
  };
  const handleApiChange = (field, value) => setFormData({ ...formData, [field]: value });

  const addPort = () => {
    if (formData.ports.length >= (userLimits?.maxPortsPerWebsite || 5)) {
      alert(`Maximum ${userLimits?.maxPortsPerWebsite || 5} ports allowed`);
      return;
    }
    setFormData({ ...formData, ports: [...formData.ports, { port: '', customMode: false }] });
  };
  const updatePort = (index, field, value) => {
    const newPorts = [...formData.ports];
    newPorts[index][field] = value;
    setFormData({ ...formData, ports: newPorts });
  };
  const removePort = (index) => {
    setFormData({ ...formData, ports: formData.ports.filter((_, i) => i !== index) });
  };

  const addApi = () => {
    if (formData.apis.length >= (userLimits?.maxApisPerWebsite || 2)) {
      alert(`Maximum ${userLimits?.maxApisPerWebsite || 2} APIs allowed`);
      return;
    }
    setFormData({ ...formData, apis: [...formData.apis, { url: '', method: 'GET', expectedStatus: 200, headers: '' }] });
  };
  const updateApi = (index, field, value) => {
    const newApis = [...formData.apis];
    newApis[index][field] = value;
    setFormData({ ...formData, apis: newApis });
  };
  const removeApi = (index) => {
    setFormData({ ...formData, apis: formData.apis.filter((_, i) => i !== index) });
  };

  const addPing = () => {
    if (formData.pings.length >= (userLimits?.maxPingsPerWebsite || 2)) {
      alert(`Maximum ${userLimits?.maxPingsPerWebsite || 2} pings allowed`);
      return;
    }
    setFormData({ ...formData, pings: [...formData.pings, { host: '' }] });
  };
  const updatePing = (index, field, value) => {
    const newPings = [...formData.pings];
    newPings[index][field] = value;
    setFormData({ ...formData, pings: newPings });
  };
  const removePing = (index) => {
    setFormData({ ...formData, pings: formData.pings.filter((_, i) => i !== index) });
  };

  const toggleNotification = (field) => setFormData({ ...formData, [field]: !formData[field] });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.type === 'WEBSITE' && !formData.url.trim()) {
      setError('URL is required');
      return;
    }
    if (formData.type === 'PORT') {
      if (!formData.port) setError('Port number is required');
      else if (!formData.url.trim()) setError('Hostname is required');
      if (error) return;
    }
    if (formData.type === 'API') {
      if (!formData.apiUrl.trim()) setError('API URL is required');
      else if (!formData.url.trim()) setError('Hostname is required');
      if (error) return;
    }
    if (formData.type === 'PING') {
      if (!formData.url.trim()) setError('Hostname is required');
      if (error) return;
    }
    if (formData.type === 'SERVICE') {
      if (formData.ports.length === 0 && formData.apis.length === 0 && formData.pings.length === 0 && !formData.url.trim()) {
        setError('Add at least one component (port, API, ping) or a website URL');
        return;
      }
    }
    if (formData.type === 'BLACKLIST') {
      if (!formData.blacklistName.trim()) {
        setError('Name is required');
        return;
      }
      if (!formData.blacklistTarget.trim()) {
        setError('Target (IP or Domain) is required');
        return;
      }
    }

    setLoading(true);
    setError('');

    if (formData.type === 'BLACKLIST') {
      const blacklistPayload = {
        name: formData.blacklistName.trim(),
        blacklistTarget: formData.blacklistTarget.trim(),
        blacklistType: formData.blacklistType,
        blacklistInterval: formData.blacklistInterval,
        isActive: formData.isActive,
        notifyEmail: formData.notifyEmail,
        notifyTelegram: formData.notifyTelegram,
      };
      const result = await onSubmit(website.id, blacklistPayload);
      if (!result.success) {
        setError(result.error || 'Failed to update blacklist monitor');
        setLoading(false);
      } else {
        onClose();
      }
      return;
    }

    const payload = {
      name: formData.name.trim(),
      url: formData.url.trim() || '',
      type: formData.type,
      isActive: formData.isActive,
      notifyEmail: formData.notifyEmail,
      notifyTelegram: formData.notifyTelegram,
      notifySsl: formData.notifySsl,
      notifyDomain: formData.notifyDomain,
      notifyDns: formData.notifyDns,
    };

    if (formData.type === 'PORT') {
      payload.ports = [{ port: parseInt(formData.port) }];
    } else if (formData.type === 'API') {
      let headers = null;
      if (formData.apiHeaders.trim()) {
        try {
          headers = JSON.parse(formData.apiHeaders);
        } catch (e) {
          setError('Invalid JSON in headers');
          setLoading(false);
          return;
        }
      }
      payload.apis = [{
        url: formData.apiUrl.trim(),
        method: formData.apiMethod,
        expectedStatus: parseInt(formData.apiExpectedStatus) || 200,
        headers,
      }];
    } else if (formData.type === 'PING') {
      payload.pings = [{ host: formData.url.trim() }];
    } else if (formData.type === 'SERVICE') {
      const cleanPorts = formData.ports.filter(p => p.port).map(p => ({ port: parseInt(p.port) }));
      const cleanApis = formData.apis.filter(a => a.url.trim()).map(a => {
        let headers = null;
        if (a.headers.trim()) {
          try {
            headers = JSON.parse(a.headers);
          } catch (e) {
            setError('Invalid JSON in headers');
            return null;
          }
        }
        return {
          url: a.url.trim(),
          method: a.method,
          expectedStatus: parseInt(a.expectedStatus) || 200,
          headers,
        };
      });
      if (cleanApis.includes(null)) return;
      const cleanPings = formData.pings.filter(p => p.host.trim()).map(p => ({ host: p.host.trim() }));
      payload.ports = cleanPorts;
      payload.apis = cleanApis;
      payload.pings = cleanPings;
    }

    const result = await onSubmit(website.id, payload);
    if (!result.success) {
      setError(result.error || 'Failed to update monitor');
      setLoading(false);
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`flex items-center justify-between p-6 border-b sticky top-0 z-10 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-white'}`}>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: greenGradient }}>
              <Save className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Edit Monitor</h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Update settings</p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-xl ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
            <X className={`h-5 w-5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="flex items-center space-x-3 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          <div>
            <label className={`block text-sm font-semibold mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Monitor Type</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {types.map((t) => {
                const Icon = t.icon;
                const isSelected = formData.type === t.value;
                return (
                  <div 
                    key={t.value} 
                    className={`flex items-center p-3 rounded-xl border-2 ${
                      isSelected
                        ? 'border-green-500'
                        : darkMode 
                          ? 'border-gray-600 bg-gray-700/50' 
                          : 'border-gray-200 bg-gray-50'
                    }`}
                    style={
                      isSelected && darkMode 
                        ? { backgroundColor: '#1a3a1a', borderColor: '#95DE1A' }
                        : isSelected && !darkMode
                        ? { backgroundColor: '#f0fdf4', borderColor: '#95DE1A' }
                        : {}
                    }
                  >
                    <Icon className={`h-5 w-5 mr-2 ${
                      isSelected 
                        ? 'text-green-600 dark:text-green-400' 
                        : darkMode ? 'text-gray-500' : 'text-gray-400'
                    }`} />
                    <span className={`text-sm font-medium ${
                      isSelected 
                        ? 'text-green-700 dark:text-green-300' 
                        : darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>{t.label}</span>
                  </div>
                );
              })}
            </div>
            <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Type cannot be changed after creation.</p>
          </div>

          {formData.type === 'BLACKLIST' && (
            <div className={`space-y-4 border-t pt-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Blacklist Configuration</h3>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name *</label>
                <input type="text" value={formData.blacklistName} onChange={(e) => setFormData({ ...formData, blacklistName: e.target.value })} className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Target *</label>
                <input type="text" value={formData.blacklistTarget} onChange={(e) => setFormData({ ...formData, blacklistTarget: e.target.value })} className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} />
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Type</label>
                <div className="flex space-x-4">
                  <label className={`flex items-center space-x-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}><input type="radio" name="blacklistType" value="IP" checked={formData.blacklistType === 'IP'} onChange={(e) => setFormData({ ...formData, blacklistType: e.target.value })} /><span>IP Address</span></label>
                  <label className={`flex items-center space-x-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}><input type="radio" name="blacklistType" value="DOMAIN" checked={formData.blacklistType === 'DOMAIN'} onChange={(e) => setFormData({ ...formData, blacklistType: e.target.value })} /><span>Domain</span></label>
                </div>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Check Interval (seconds)</label>
                <input type="number" min="60" max="86400" value={formData.blacklistInterval} onChange={(e) => setFormData({ ...formData, blacklistInterval: parseInt(e.target.value) || 300 })} className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} />
              </div>
            </div>
          )}

          {formData.type !== 'BLACKLIST' && (
            <div>
              <label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Name *</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} />
            </div>
          )}

          {formData.type === 'WEBSITE' && (
            <div><label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>URL *</label><div className="relative"><Globe className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} /><input type="text" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} /></div></div>
          )}

          {(formData.type === 'PORT' || formData.type === 'API' || formData.type === 'PING') && (
            <div><label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Hostname *</label><div className="relative"><Globe className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} /><input type="text" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} /></div></div>
          )}

          {formData.type === 'SERVICE' && (
            <div><label className={`block text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Website URL (optional)</label><div className="relative"><Globe className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} /><input type="text" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} /></div></div>
          )}

          <div className={`rounded-xl p-4 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
            <div className="flex items-center justify-between">
              <div><label className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Enable Monitoring</label><p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Turn on/off all checks</p></div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} className="sr-only peer" />
                <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all" style={{ backgroundColor: formData.isActive ? greenColor : '#D1D5DB' }}></div>
              </label>
            </div>
          </div>

          {formData.type === 'PORT' && (
            <div className={`space-y-4 border-t pt-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Port Configuration</h3>
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Port Number *</label>
                {!formData.portCustomMode ? (
                  <select value={formData.port} onChange={handlePortSelect} className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}>
                    <option value="">Select a common port</option>
                    {commonPorts.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    <option value="custom">Custom port</option>
                  </select>
                ) : (
                  <div className="flex space-x-2">
                    <input type="number" min="1" max="65535" value={formData.port} onChange={(e) => handlePortChange(e.target.value)} className={`flex-1 px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} placeholder="Enter port number" autoFocus />
                    <button type="button" onClick={() => setFormData({ ...formData, portCustomMode: false, port: '' })} className={`px-4 py-3 rounded-xl ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Back</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {formData.type === 'API' && (
            <div className={`space-y-4 border-t pt-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>API Configuration</h3>
              <div><label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>API URL *</label><input type="text" value={formData.apiUrl} onChange={(e) => handleApiChange('apiUrl', e.target.value)} className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Method</label><select value={formData.apiMethod} onChange={(e) => handleApiChange('apiMethod', e.target.value)} className={`w-full px-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`}><option>GET</option><option>POST</option><option>PUT</option><option>DELETE</option><option>HEAD</option></select></div>
                <div><label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Expected Status</label><input type="number" value={formData.apiExpectedStatus} onChange={(e) => handleApiChange('apiExpectedStatus', e.target.value)} className={`w-full px-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} /></div>
              </div>
              <div><label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Headers (JSON)</label><textarea value={formData.apiHeaders} onChange={(e) => handleApiChange('apiHeaders', e.target.value)} rows="4" className={`w-full px-3 py-2 border rounded-xl font-mono text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} placeholder='{"Authorization": "Bearer token"}' /></div>
            </div>
          )}

          {formData.type === 'PING' && (
            <div className={`space-y-4 border-t pt-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}><h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>Ping Configuration</h3><p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Ping monitor is always enabled.</p></div>
          )}

          {formData.type === 'SERVICE' && (
            <div className={`space-y-6 border-t pt-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div><div className="flex items-center justify-between mb-4"><h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-700'}`}>Ports (max {userLimits?.maxPortsPerWebsite || 5})</h3><button type="button" onClick={addPort} disabled={formData.ports.length >= (userLimits?.maxPortsPerWebsite || 5)} className="px-3 py-1.5 text-sm text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center space-x-1" style={{ background: greenGradient }}><Plus className="h-4 w-4" /><span>Add Port</span></button></div>
              {formData.ports.length === 0 && <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No ports configured.</p>}
              {formData.ports.map((port, index) => (
                <div key={index} className={`flex items-center space-x-2 mb-3 p-3 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex-1">
                    {port.customMode ? (
                      <input type="number" min="1" max="65535" value={port.port} onChange={(e) => updatePort(index, 'port', e.target.value)} className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'}`} placeholder="Enter port number" autoFocus />
                    ) : (
                      <select value={port.port} onChange={(e) => { const val = e.target.value; if (val === 'custom') { updatePort(index, 'customMode', true); updatePort(index, 'port', ''); } else { updatePort(index, 'port', val); updatePort(index, 'customMode', false); } }} className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'}`}>
                        <option value="">Select port</option>
                        {commonPorts.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        <option value="custom">Custom port</option>
                      </select>
                    )}
                  </div>
                  <button type="button" onClick={() => removePort(index)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              </div>
              <div><div className="flex items-center justify-between mb-4"><h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-700'}`}>APIs (max {userLimits?.maxApisPerWebsite || 2})</h3><button type="button" onClick={addApi} disabled={formData.apis.length >= (userLimits?.maxApisPerWebsite || 2)} className="px-3 py-1.5 text-sm text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center space-x-1" style={{ background: greenGradient }}><Plus className="h-4 w-4" /><span>Add API</span></button></div>
              {formData.apis.length === 0 && <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No APIs configured.</p>}
              {formData.apis.map((api, index) => (
                <div key={index} className={`mb-4 p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <div className="flex items-start justify-between mb-2"><h4 className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>API #{index + 1}</h4><button onClick={() => removeApi(index)} className="p-1 text-red-600 hover:bg-red-50 rounded"><Trash2 className="h-4 w-4" /></button></div>
                  <div className="space-y-3">
                    <input type="text" placeholder="API URL" value={api.url} onChange={(e) => updateApi(index, 'url', e.target.value)} className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'}`} />
                    <div className="grid grid-cols-2 gap-3">
                      <select value={api.method} onChange={(e) => updateApi(index, 'method', e.target.value)} className={`px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'}`}><option>GET</option><option>POST</option><option>PUT</option><option>DELETE</option><option>HEAD</option></select>
                      <input type="number" placeholder="Expected Status" value={api.expectedStatus} onChange={(e) => updateApi(index, 'expectedStatus', e.target.value)} className={`px-3 py-2 border rounded-lg ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'}`} />
                    </div>
                    <textarea placeholder='Headers (JSON)' value={api.headers} onChange={(e) => updateApi(index, 'headers', e.target.value)} rows="3" className={`w-full px-3 py-2 border rounded-lg font-mono text-sm ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'}`} />
                  </div>
                </div>
              ))}
              </div>
              <div><div className="flex items-center justify-between mb-4"><h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-700'}`}>Pings (max {userLimits?.maxPingsPerWebsite || 2})</h3><button type="button" onClick={addPing} disabled={formData.pings.length >= (userLimits?.maxPingsPerWebsite || 2)} className="px-3 py-1.5 text-sm text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center space-x-1" style={{ background: greenGradient }}><Plus className="h-4 w-4" /><span>Add Ping</span></button></div>
              {formData.pings.length === 0 && <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>No pings configured.</p>}
              {formData.pings.map((ping, index) => (
                <div key={index} className={`flex items-center space-x-2 mb-3 p-3 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <input type="text" value={ping.host} onChange={(e) => updatePing(index, 'host', e.target.value)} className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'}`} placeholder="Host (IP or domain)" />
                  <button type="button" onClick={() => removePing(index)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              </div>
            </div>
          )}

          <div className={`border-t pt-6 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>Notification Preferences</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div onClick={() => toggleNotification('notifyEmail')} className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${formData.notifyEmail ? (darkMode ? 'bg-green-900/40 text-green-400 border border-green-500/50' : 'bg-green-100 text-green-700 border border-green-200') : (darkMode ? 'bg-gray-700 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200')}`}>
                <Mail className="h-4 w-4" /><span className="text-sm font-medium">Email</span>{formData.notifyEmail ? <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" /> : <XCircle className="h-4 w-4 text-red-500" />}
              </div>
              <div onClick={() => toggleNotification('notifyTelegram')} className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${formData.notifyTelegram ? (darkMode ? 'bg-sky-900/40 text-sky-400 border border-sky-500/50' : 'bg-blue-100 text-blue-700 border border-blue-200') : (darkMode ? 'bg-gray-700 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200')}`}>
                <MessageCircle className="h-4 w-4" /><span className="text-sm font-medium">Telegram</span>{formData.notifyTelegram ? <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" /> : <XCircle className="h-4 w-4 text-red-500" />}
              </div>
              {formData.type !== 'BLACKLIST' && (
                <>
                  <div onClick={() => toggleNotification('notifySsl')} className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${formData.notifySsl ? (darkMode ? 'bg-amber-900/40 text-amber-400 border border-amber-500/50' : 'bg-yellow-100 text-yellow-700 border border-yellow-200') : (darkMode ? 'bg-gray-700 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200')}`}>
                    <Shield className="h-4 w-4" /><span className="text-sm font-medium">SSL</span>{formData.notifySsl ? <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" /> : <XCircle className="h-4 w-4 text-red-500" />}
                  </div>
                  <div onClick={() => toggleNotification('notifyDomain')} className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${formData.notifyDomain ? (darkMode ? 'bg-purple-900/40 text-purple-400 border border-purple-500/50' : 'bg-purple-100 text-purple-700 border border-purple-200') : (darkMode ? 'bg-gray-700 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200')}`}>
                    <Globe2 className="h-4 w-4" /><span className="text-sm font-medium">Domain</span>{formData.notifyDomain ? <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" /> : <XCircle className="h-4 w-4 text-red-500" />}
                  </div>
                  <div onClick={() => toggleNotification('notifyDns')} className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all ${formData.notifyDns ? (darkMode ? 'bg-indigo-900/40 text-indigo-400 border border-indigo-500/50' : 'bg-indigo-100 text-indigo-700 border border-indigo-200') : (darkMode ? 'bg-gray-700 text-gray-400 border border-gray-600' : 'bg-gray-100 text-gray-500 border border-gray-200')}`}>
                    <Activity className="h-4 w-4" /><span className="text-sm font-medium">DNS</span>{formData.notifyDns ? <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" /> : <XCircle className="h-4 w-4 text-red-500" />}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className={`p-3 rounded-xl text-xs flex items-start space-x-2 ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700'}`}>
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span><strong>Firewall Recommendation:</strong> Allow our monitoring IP <code className={`px-1 py-0.5 rounded ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>85.235.75.96</code>.</span>
          </div>

          <div className="flex space-x-4 pt-6">
            <button type="button" onClick={onClose} className={`flex-1 px-6 py-3 text-sm font-semibold rounded-2xl transition-all ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 px-6 py-3 text-sm font-semibold text-white rounded-2xl transition-all disabled:opacity-50" style={{ background: greenGradient }}>
              {loading ? <div className="flex items-center justify-center space-x-2"><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div><span>Saving...</span></div> : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditWebsiteModal;